/**
 * THE MESSAGE, PREPARED — NEVER SENT.
 *
 * Assisted communication, deliberately. Nothing here talks to an email server
 * and nothing claims a message was sent: the administrator copies it, or opens
 * their own mail client with it, and sends it as themselves. Building delivery
 * would mean deciding what "from StandingDocs" means on a message a person
 * wrote, and that is a decision to take on purpose rather than by accident.
 *
 * Pure, so every wording is a test.
 */

import type { Requirement } from './types';

export interface PreparedMessage {
  to: string | null;
  toName: string | null;
  subject: string;
  body: string;
}

/** The role labels as the console shows them. Matches billing/contacts.ts. */
export const ROLE_LABEL: Record<string, string> = {
  champion: 'Champion',
  signatory: 'Signatory',
  billing: 'Billing',
  legal: 'Legal',
  procurement: 'Procurement',
  security: 'Information security',
  privacy: 'Privacy officer',
};

export function prepareMessage(input: {
  orgName: string;
  requirements: Requirement[];
  recipient: { name: string; email: string } | null;
  /** The customer's own onboarding form, when one is live. */
  intakeLinkSent: boolean;
}): PreparedMessage {
  const { orgName, requirements, recipient } = input;
  const first = requirements[0];

  const subject = requirements.length === 1 && first
    ? `StandingDocs onboarding — ${first.label.toLowerCase()} needed`
    : `StandingDocs onboarding — ${orgName}`;

  const greeting = recipient?.name ? `Hi ${recipient.name.split(' ')[0]},` : 'Hello,';
  const items = requirements.map((r) => `• ${r.label}`).join('\n');

  // Only mentioned when one actually exists. Telling somebody to use a link
  // they were never sent is worse than not mentioning it.
  const linkLine = input.intakeLinkSent
    ? '\n\nIf it is easier, you can update this on the onboarding form we sent you '
      + '— the link in that email still works.'
    : '';

  const body = `${greeting}

We are setting up ${orgName} on StandingDocs and still need the following:

${items}${linkLine}

If anything here is unclear, just reply and we will explain what it is for.

Thank you.`;

  return {
    to: recipient?.email ?? null,
    toName: recipient?.name ?? null,
    subject,
    body,
  };
}

/** A mailto the administrator's own client opens, so they send it as themselves. */
export function mailtoFor(message: PreparedMessage): string {
  const params = new URLSearchParams({ subject: message.subject, body: message.body });
  return `mailto:${message.to ?? ''}?${params.toString().replace(/\+/g, '%20')}`;
}
